<xemex-dialog-shell [title]="(isEdit ? 'editUser.title' : 'createNewUser.title') | translate">
  @switch (stepSelected) {
    @case (CreateStepEnum.UserInformation) {
      <section class="user-dialog__content">
        <div class="user-dialog__section-title">
          {{ 'createNewUser.userInfor.title' | translate }}
        </div>

        <form class="user-dialog__form-grid" [formGroup]="userForm">
          <xemex-text-field
            formControlName="firstName"
            [label]="'createNewUser.userInfor.firstName' | translate"
            [placeholder]="'general.placeholderInput' | translate"
            [required]="true"
            [invalid]="hasError('firstName', 'required')"
            [errorText]="
              'error.required'
                | translate: { field: ('createNewUser.userInfor.firstName' | translate) }
            "
          />

          <xemex-text-field
            formControlName="lastName"
            [label]="'createNewUser.userInfor.lastName' | translate"
            [placeholder]="'general.placeholderInput' | translate"
            [required]="true"
            [invalid]="hasError('lastName', 'required')"
            [errorText]="
              'error.required'
                | translate: { field: ('createNewUser.userInfor.lastName' | translate) }
            "
          />

          <xemex-text-field
            formControlName="email"
            [label]="'createNewUser.userInfor.emailAddress' | translate"
            [placeholder]="'general.placeholderInput' | translate"
            [type]="'email'"
            [required]="true"
            [invalid]="hasError('email', 'required') || hasError('email', 'email')"
            [errorText]="
              hasError('email', 'required')
                ? ('error.required'
                  | translate: { field: ('createNewUser.userInfor.emailAddress' | translate) })
                : ('error.emailInvalid' | translate)
            "
          />

          <xemex-text-field
            formControlName="phoneNumber"
            [label]="'createNewUser.userInfor.phoneNumber' | translate"
            [placeholder]="'general.placeholderInput' | translate"
          />
        </form>
      </section>
    }

    @case (CreateStepEnum.UserRights) {
      <section class="user-dialog__content user-dialog__content--tall">
        <div class="user-dialog__rights-header">
          <div>
            <div class="user-dialog__section-title user-dialog__section-title--compact">
              {{ 'createNewUser.userRights.title' | translate }}
            </div>
            <div class="user-dialog__section-description">
              {{ 'createNewUser.userRights.desc' | translate }}
            </div>
          </div>

          @if (userStore.isLoading()) {
            <xemex-skeleton-block [width]="'206px'" [height]="'41px'"></xemex-skeleton-block>
          } @else {
            <xemex-select-field
              class="user-dialog__role-field"
              [label]="'createNewUser.userInfor.userRole' | translate"
            >
              <mat-select
                [formControl]="userForm.controls.userRole"
                [compareWith]="compareRole"
                [placeholder]="'general.placeholderInput' | translate"
              >
                @for (role of roles; track role.roleId) {
                  <mat-option [value]="role">
                    {{ 'roles.roleNames.' + role.roleName.toLowerCase() | translate }}
                  </mat-option>
                }
              </mat-select>
            </xemex-select-field>
          }
        </div>

        <div class="user-dialog__feature-card">
          @if (userStore.isLoadingFeatures()) {
            <div class="user-dialog__feature-grid">
              @for (column of [1, 2, 3, 4, 5]; track column) {
                <div>
                  <xemex-skeleton-block
                    [width]="'206px'"
                    [height]="'32px'"
                    [color]="'primary'"
                    class="mb-3"
                  ></xemex-skeleton-block>
                  @for (row of [1, 2, 3, 4, 5]; track row) {
                    <xemex-skeleton-block [height]="'32px'"></xemex-skeleton-block>
                  }
                </div>
              }
            </div>
          } @else {
            <div class="user-dialog__feature-grid">
              @for (item of roleFeatures; track $index) {
                <div>
                  <div class="user-dialog__feature-group-title">
                    <mat-icon class="text-primary">people</mat-icon>
                    @let groupKey = 'roles.featureGroup.' + toRoleKey(item.featureGroupName);
                    @let groupLabel = groupKey | translate;
                    <span>{{ labelOrFallback(groupLabel, groupKey, item.featureGroupName) }}</span>
                  </div>

                  <div class="user-dialog__feature-list">
                    @for (feature of item.features; track feature.featureName) {
                      <div class="user-dialog__feature-item">
                        <mat-icon
                          class="text-primary text-xl h-5 w-5"
                          [class.!text-neutral]="!feature.featureEnabled"
                        >
                          check_circle
                        </mat-icon>
                        @let featureKey = 'roles.features.' + toRoleKey(feature.featureName);
                        @let featureLabel = featureKey | translate;
                        <span>{{
                          labelOrFallback(featureLabel, featureKey, humanize(feature.featureName))
                        }}</span>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>

        @if (submitError) {
          <div class="flex items-center gap-2 rounded-xl bg-status-error p-3 text-error">
            <mat-icon>error_outline</mat-icon>
            <span>{{
              (isEdit ? 'editUser.errorText' : 'createNewUser.errorText') | translate
            }}</span>
          </div>
        }
      </section>
    }

    @default {
      <section class="user-dialog__content">
        <div class="user-dialog__result-card">
          <div class="user-dialog__result-copy">
            @if (isEdit) {
              <div>{{ 'editUser.completed.desc1' | translate }}</div>
            } @else {
              <div>{{ 'createNewUser.completed.desc1' | translate: { company: companyName } }}</div>
              <div>{{ 'createNewUser.completed.desc2' | translate }}</div>
            }
          </div>

          <div class="user-dialog__result-avatar">
            @if (isEdit) {
              <img
                xemexFetchImage
                [src]="$safeNavigationMigration(user?.avatar)"
                [dummySrc]="'assets/images/illustration/dummy-avatar.jpg'"
                alt="avatar"
                class="user-dialog__photo-image"
              />
            } @else {
              <img
                class="user-dialog__photo-image"
                [src]="'assets/images/illustration/dummy-avatar.jpg'"
              />
            }
          </div>

          <div class="user-dialog__result-name">{{ isEdit ? user?.fullName : fullName }}</div>

          <div class="user-dialog__result-meta-stack">
            <strong>{{ 'createNewUser.completed.username' | translate }}:</strong>
            <span>{{ isEdit ? user?.email : email }}</span>
          </div>
        </div>
      </section>
    }
  }

  <div actions class="user-dialog__actions">
    @switch (stepSelected) {
      @case (CreateStepEnum.UserInformation) {
        <xemex-text-button type="button" intent="primary" (click)="dialogRef.close()">
          {{ 'general.btnCancel' | translate }}
        </xemex-text-button>

        <div class="user-dialog__action-group">
          <xemex-outlined-button type="button" (click)="onNext()">
            {{ 'general.btnNext' | translate }}
          </xemex-outlined-button>
        </div>
      }

      @case (CreateStepEnum.UserRights) {
        <xemex-text-button type="button" intent="primary" (click)="dialogRef.close()">
          {{ 'general.btnCancel' | translate }}
        </xemex-text-button>

        <div class="user-dialog__action-group">
          <xemex-filled-button
            type="button"
            (click)="onSubmit()"
            [disabled]="!userForm.controls.userRole.value || isSubmitting"
          >
            <div class="flex">
              <span class="mr-2">
                {{ (isEdit ? 'general.btnEdit' : 'general.btnCreate') | translate }}
              </span>
              @if (isSubmitting) {
                <xemex-spinner size="sm"></xemex-spinner>
              }
            </div>
          </xemex-filled-button>
        </div>
      }

      @default {
        @if (!isEdit) {
          <xemex-outlined-button type="button" (click)="onAddAnotherUser()">
            {{ 'createNewUser.completed.btnAddAnotherUser' | translate }}
          </xemex-outlined-button>
        } @else {
          <div></div>
        }

        <div class="user-dialog__action-group">
          <xemex-filled-button type="button" (click)="onClose()">
            {{ 'general.btnClose' | translate }}
          </xemex-filled-button>
        </div>
      }
    }
  </div>
</xemex-dialog-shell>
